import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  Skeleton,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  OrgPublicProfileDto,
  OrganizationEntryDto,
  SupplierStanding,
  useListBuyerEventsWithSupplierQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { HorizontalScrollBox } from "../../../../common/HorizontalScrollBox";
import { ListLoadError } from "../../../../common/ListLoadError";
import { Paging } from "../../../../common/Paging";
import { SupplierStandingTag } from "./SupplierStandingTag";
import { useEventNavigation } from "../../../sourcing-events/useEventNavigation";
import { EventListItemStatusTag } from "../../../sourcing-event/EventListItemStatusTag";

const PAGE_SIZE = 10;
const COLUMN_COUNT = 3;

interface SourcingEventsSectionProps {
  organization?: OrgPublicProfileDto;
  organizationEntry?: OrganizationEntryDto;
}

export const SourcingEventsSection = ({ organization, organizationEntry }: SourcingEventsSectionProps) => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const navigateToEvent = useEventNavigation();
  const [skip, setSkip] = useState(0);

  const orgData = organization || organizationEntry;
  const supplier = organization
    ? { supplierOrganizationId: organization.id }
    : organizationEntry
      ? { supplierEntryId: organizationEntry.id }
      : undefined;

  const { data, isLoading, isFetching, isError, refetch } = useListBuyerEventsWithSupplierQuery(
    supplier ? { orgId: authState.selectedOrg.id, ...supplier, limit: PAGE_SIZE, skip } : skipToken
  );
  const events = data?.events ?? [];

  if (!orgData) throw new Error("No organization data");

  return (
    <Box width="full" height="full">
      <VStack spacing={6} align="flex-start" width="full">
        <Heading size="md" pt="6">
          {t("Sourcing Events")}
        </Heading>
        <Card width="full">
          <CardHeader>
            <Text fontWeight="semibold">
              {organization?.linkedEntries?.length
                ? t("Sourcing events with {{name}} and linked organizations", { name: orgData.name })
                : t("Sourcing events with {{name}}", { name: orgData.name })}
            </Text>
          </CardHeader>
          <CardBody>
            {isError && !isFetching ? (
              <ListLoadError message={t("Could not load sourcing events.")} onRetry={refetch} />
            ) : (
              <Flex
                direction="column"
                bg="smBackground"
                border="1px solid"
                borderColor="smBorder"
                borderRadius="lg"
                width="full"
              >
                <HorizontalScrollBox>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th minWidth="80">{t("Title")}</Th>
                        <Th>{t("Status")}</Th>
                        <Th>{t("Bid")}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {isLoading
                        ? Array.from({ length: 3 }, (_, rowIndex) => (
                            <Tr key={rowIndex}>
                              {Array.from({ length: COLUMN_COUNT }, (_, cellIndex) => (
                                <Td key={cellIndex}>
                                  <Skeleton h="5" w="full" rounded="md" />
                                </Td>
                              ))}
                            </Tr>
                          ))
                        : events.map(({ event, bidSubmitted, standing }) => (
                            <Tr
                              key={event.id}
                              cursor="pointer"
                              _hover={{ bg: "smBackgroundSecondary" }}
                              onClick={() => navigateToEvent(event)}
                            >
                              <Td fontWeight="semibold">{event.title || t("Untitled")}</Td>
                              <Td>
                                <EventListItemStatusTag event={event} size="sm" />
                              </Td>
                              <Td>
                                {standing && standing !== SupplierStanding.NotDelivered ? (
                                  <SupplierStandingTag standing={standing} />
                                ) : (
                                  <Tag colorScheme={bidSubmitted ? "purple" : "gray"} size="sm" textAlign="center" py="2">
                                    {bidSubmitted ? t("Bid submitted") : t("No bid submitted")}
                                  </Tag>
                                )}
                              </Td>
                            </Tr>
                          ))}
                    </Tbody>
                  </Table>
                </HorizontalScrollBox>
                {!isLoading && events.length === 0 && (
                  <Flex justify="center" py="10" px="4" textAlign="center">
                    <Text color="smMuted">{t("No sourcing events with {{name}} yet.", { name: orgData.name })}</Text>
                  </Flex>
                )}
                {(data?.count ?? 0) > PAGE_SIZE && (
                  <Paging skip={skip} limit={PAGE_SIZE} setSkip={setSkip} isLoading={isFetching} count={data?.count} />
                )}
              </Flex>
            )}
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};
